import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './App.css';
import MultitrackPlayer from './MultitrackPlayer';
import { table1Examples, modelConfigs, getAudioPath, loadExampleMetadata } from './table1Data';
import { table2Examples, table2ModelConfigs, getTable2AudioPath, loadTable2ExampleMetadata, getGenerationTime } from './table2Data';
import { composerExamples, getComposerAudioPath, loadComposerExampleMetadata, getCurrentVariation, loadVariationMetadata } from './composerData';

// Wrapper component to memoize tracks preparation
const MultitrackPlayerWrapper = React.memo(({ modelConfig, selectedExample, exampleMetadata, prepareTracksForModel, getStemIcon, generationTimes }) => {
  const tracks = useMemo(() => {
    if (!exampleMetadata || !selectedExample) {
      return [];
    }
    return prepareTracksForModel(selectedExample, modelConfig, exampleMetadata);
  }, [selectedExample, modelConfig, exampleMetadata, prepareTracksForModel]);

  console.log(`MultitrackPlayerWrapper: Rendering for ${modelConfig.displayName} with ${tracks.length} tracks`);

  return (
    <div className="multitrack-player-wrapper">
      <MultitrackPlayer
        key={`${selectedExample.id}-${modelConfig.id}`} // Force re-render when example changes
        tracks={tracks}
        modelConfig={modelConfig}
        selectedExample={selectedExample}
        getStemIcon={getStemIcon}
        generationTime={generationTimes ? generationTimes[modelConfig.id] : undefined}
      />
    </div>
  );
});


// Table 2: Wrapper to memoize tracks preparation
const Table2MultitrackPlayerWrapper = React.memo(({ modelConfig, selectedExample, exampleMetadata, prepareTracksForTable2Model, getStemIcon, generationTimes }) => {
  const tracks = useMemo(() => {
    if (!exampleMetadata || !selectedExample) {
      return [];
    }
    return prepareTracksForTable2Model(selectedExample, modelConfig, exampleMetadata);
  }, [selectedExample, modelConfig, exampleMetadata, prepareTracksForTable2Model]);

  return (
    <div className="multitrack-player-wrapper">
      <MultitrackPlayer
        key={`${selectedExample.id}-${modelConfig.id}`}
        tracks={tracks}
        modelConfig={modelConfig}
        selectedExample={selectedExample}
        getStemIcon={getStemIcon}
        generationTime={generationTimes ? generationTimes[modelConfig.id] : undefined}
      />
    </div>
  );
});

function App() {
  const [activeSubpage, setActiveSubpage] = useState('main-ablation');
  const [selectedExample, setSelectedExample] = useState(table1Examples[0]);
  const [exampleMetadata, setExampleMetadata] = useState(null);
  const [selectedTable2Example, setSelectedTable2Example] = useState(table2Examples[0]);
  const [table2ExampleMetadata, setTable2ExampleMetadata] = useState(null);
  const [generationTimes, setGenerationTimes] = useState({});
  const [selectedComposerExample, setSelectedComposerExample] = useState(composerExamples[0]);
  const [selectedVariation, setSelectedVariation] = useState('a');
  const [composerExampleMetadata, setComposerExampleMetadata] = useState(null);
  const [showPass1Prompt, setShowPass1Prompt] = useState(false);
  const [pinnedVisualization, setPinnedVisualization] = useState(null);
  const [hoveredVisualization, setHoveredVisualization] = useState(null);
  const [hoveredDisabled, setHoveredDisabled] = useState(null);
  const isHoveringRef = useRef(false);
  
  // New state for player mode toggle
  const [useMultitrackPlayer, setUseMultitrackPlayer] = useState(false);
  
  // State for abstract and bibtex
  const [showAbstract, setShowAbstract] = useState(false);
  const [abstractText, setAbstractText] = useState('');
  const [bibtexText, setBibtexText] = useState('');
  const bibtexRef = useRef(null);
  const soundExamplesRef = useRef(null);
  

  // Function to stop all active MultitrackPlayer instances by clicking their STOP buttons
  const stopAllMultitrackPlayers = useCallback(() => {
    console.log('🛑 Attempting to stop all multitrack players...');
    
    // Try multiple selectors to find stop buttons
    const selectors = [
      '.multitrack-player .stop-btn',
      '.multitrack-player-wrapper .stop-btn', 
      '.stop-btn',
      'button[class*="stop"]'
    ];
    
    let totalStopped = 0;
    selectors.forEach(selector => {
      try {
        const stopButtons = document.querySelectorAll(selector);
        console.log(`🔍 Found ${stopButtons.length} buttons with selector: ${selector}`);
        stopButtons.forEach(button => {
          try {
            // Check if it's actually a stop button by text content
            const buttonText = button.textContent?.toLowerCase() || '';
            if (buttonText.includes('stop') || button.classList.contains('stop-btn')) {
              button.click();
              totalStopped++;
              console.log(`🛑 Clicked stop button: ${buttonText}`);
            }
          } catch (error) {
            console.warn('Error clicking individual stop button:', error);
          }
        });
      } catch (error) {
        console.warn(`Error with selector ${selector}:`, error);
      }
    });
    
    console.log(`🛑 Total stop buttons clicked: ${totalStopped}`);
  }, []);

  // Stop all players when subpage, example, or variation changes
  useEffect(() => {
    stopAllMultitrackPlayers();
  }, [activeSubpage, selectedExample.id, selectedTable2Example.id, selectedComposerExample.id, selectedVariation, stopAllMultitrackPlayers]);

  // Load abstract and bibtex text files
  useEffect(() => {
    const loadTextFiles = async () => {
      try {
        // Use relative path that works in both dev and production
        const publicUrl = process.env.PUBLIC_URL || '';
        const abstractResponse = await fetch(`${publicUrl}/text/abstract.txt`);
        if (!abstractResponse.ok) {
          throw new Error(`Failed to fetch abstract: ${abstractResponse.status}`);
        }
        const abstractContent = await abstractResponse.text();
        setAbstractText(abstractContent);

        const bibtexResponse = await fetch(`${publicUrl}/text/citation.txt`);
        if (!bibtexResponse.ok) {
          throw new Error(`Failed to fetch citation: ${bibtexResponse.status}`);
        }
        const bibtexContent = await bibtexResponse.text();
        setBibtexText(bibtexContent);
      } catch (error) {
        console.error('Failed to load text files:', error);
        // Set fallback content if files fail to load
        setAbstractText('Music stem generation, the task of producing musically-synchronized and isolated instrument audio clips, offers the potential of greater user control and better alignment with musician workflows compared to conventional text-to-music models. Existing stem generation approaches, however, either rely on fixed architectures that output a predefined set of stems in parallel, or generate only one stem at a time, resulting in slow inference despite flexibility in stem combination. We propose Stemphonic, a diffusion-/flow-based framework that overcomes this trade-off and generates a variable set of synchronized stems in one inference pass. During training, we treat each stem as a batch element, group synchronized stems in a batch, and apply a shared noise latent to each group. At inference-time, we use a shared initial noise latent and stem-specific text inputs to generate synchronized multi-stem outputs in one pass. We further expand our approach to enable one-pass conditional multi-stem generation and stem-wise activity controls to empower users to iteratively generate and orchestrate the temporal layering of a mix. We benchmark our results on multiple open-source stem evaluation sets and show that Stemphonic produces higher-quality outputs while accelerating the full mix generation process by 25 to 50%.');
        setBibtexText('@inproceedings{Wu2026Stemphonic,\n    title={Stemphonic: All-at-once Flexible Multi-stem Music Generation}, \n    author={Wu, Shih-Lun and Zhu, Ge and Caceres, Juan-Pablo and Huang, Cheng-Zhi Anna and Bryan, Nicholas J.},\n    booktitle={Proc. Int. Conf. on Acoustics, Speech, and Signal Processing (ICASSP)},\n    year={2026}\n}');
      }
    };
    
    loadTextFiles();
  }, []);

  // Helper functions for abstract and bibtex
  const scrollToBibtex = () => {
    bibtexRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToSoundExamples = () => {
    soundExamplesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleAbstract = () => {
    setShowAbstract(!showAbstract);
  };

  // Helper function to check if activity control exists for a stem (now uses current variation)
  const hasActivityControl = (example, stemType) => {
    const currentVariation = getCurrentVariation(example, selectedVariation);
    // Based on the metadata, check if the stem has activity control
    if (!currentVariation.metadata || !currentVariation.metadata.active_control_list) {
      return false;
    }
    
    // Check if there are any non-zero values in active_control_list
    const activeControlList = currentVariation.metadata.active_control_list;
    if (!Array.isArray(activeControlList) || activeControlList.length === 0) {
      return false;
    }
    
    // If there are non-zero values, activity control is available
    return activeControlList.some(value => value !== 0);
  };

  // Helper function to check if activity control exists for a 1st pass stem
  const hasPass1ActivityControl = (example, stemType) => {
    // Check if pass1Metadata exists and has activity control
    if (!example.pass1Metadata || !example.pass1Metadata.active_control_list) {
      return false;
    }
    
    // Check if there are any non-zero values in active_control_list
    const activeControlList = example.pass1Metadata.active_control_list;
    if (!Array.isArray(activeControlList) || activeControlList.length === 0) {
      return false;
    }
    
    // If there are non-zero values, activity control is available
    return activeControlList.some(value => value !== 0);
  };

  // Helper function to get visualization image path (now uses current variation)
  const getVisualizationPath = (example, stemType) => {
    const currentVariation = getCurrentVariation(example, selectedVariation);
    const basePath = `${process.env.PUBLIC_URL}/audio/${currentVariation.basePath}`;
    return `${basePath}/${stemType}/gen_visualization.jpg`;
  };

  // Helper function to get 1st pass visualization image path (two levels up from cond_gen)
  const getPass1VisualizationPath = (example, stemType) => {
    // Go two levels up from cond_gen folder to get to the base folder
    const basePathParts = example.baseFolder.split('/');
    const basePath = `${process.env.PUBLIC_URL}/audio/${basePathParts.join('/')}`;
    return `${basePath}/${stemType}/gen_visualization.jpg`;
  };

  // Handle wave icon hover enter
  const handleWaveHover = (stemType, pass = 'pass2') => {
    const visualizationKey = `${pass}-${stemType}`;
    // Only trigger if not already hovering, nothing is pinned, and not pinned to this specific item
    if (!isHoveringRef.current && !pinnedVisualization && pinnedVisualization !== visualizationKey) {
      isHoveringRef.current = true;
      setHoveredVisualization(visualizationKey);
    }
  };

  // Handle wave icon hover leave
  const handleWaveLeave = () => {
    // Only trigger if currently hovering and not pinned
    if (isHoveringRef.current && !pinnedVisualization) {
      isHoveringRef.current = false;
      setHoveredVisualization(null);
    }
  };

  // Handle wave icon click (toggle pin)
  const handleWaveClick = (stemType, pass = 'pass2') => {
    const visualizationKey = `${pass}-${stemType}`;
    if (pinnedVisualization === visualizationKey) {
      setPinnedVisualization(null);
    } else {
      // Close any existing pinned visualization
      setPinnedVisualization(visualizationKey);
      setHoveredVisualization(null);
      isHoveringRef.current = false;
    }
  };

  // Handle disabled button hover
  const handleDisabledHover = (stemType, pass = 'pass2') => {
    const key = `${pass}-${stemType}`;
    setHoveredDisabled(key);
  };

  // Handle disabled button hover leave
  const handleDisabledLeave = () => {
    setHoveredDisabled(null);
  };

  // Function to get icon for each stem type
  const getStemIcon = useCallback((stemType) => {
    // Normalize the stem type (remove underscores, uppercase)
    const normalizedType = stemType.replace(/_/g, ' ').toUpperCase();

    const icons = {
      'VOCALS': '🎤',
      'SYNTH VOCALS': '🎛️',
      'BASSES': '🎸',
      'GUITARS': '🎸',
      'ELECTRIC GUITARS': '🎸',
      'ELECTRIC GUITAR': '🎸',
      'KEYS': '🎹',
      'DRUMS': '🥁',
      'SYNTHS': '🎛️',
      'PIANOS': '🎹',
      'STRINGS': '🎻',
      'BRASS': '🎺',
      'WOODWINDS': '🎷',
      'WINDS': '🎺',
      'WIND': '🎺',
      'SFX': '🎛️',
      'EFFECTS': '🎛️',
      'FLUTE': '🪈',
      'SAXOPHONE': '🎷',
      'TRUMPET': '🎺',
      'TROMBONE': '🎺',
      'VIOLIN': '🎻',
      'CELLO': '🎻',
      'HARP': '🪕',
      'ORGAN': '🎹',
      'ACCORDION': '🪗',
      'HARMONICA': '🎵',
      'PERCUSSION': '🥁',
      'TIMPANI': '🥁',
      'XYLOPHONE': '🎵',
      'MARIMBA': '🎵',
      'VIBRAPHONE': '🎵'
    };
    return icons[normalizedType] || '🎵';
  }, []);

  // Function to prepare track data for multitrack player (Table 1)
  const prepareTracksForModel = useCallback((selectedExample, modelConfig, exampleMetadata) => {
    if (!exampleMetadata || !exampleMetadata.orderedStems) return [];
    
    const tracks = exampleMetadata.orderedStems.map((stem) => {
      const audioPath = getAudioPath(selectedExample, modelConfig.id, stem.folder);
      console.log(`Preparing track for ${modelConfig.id} - ${stem.stemType}: ${audioPath}`);
      return {
        src: audioPath,
        name: stem.stemType,
        icon: getStemIcon(stem.stemType),
        stemType: stem.stemType
      };
    });

    return tracks;
  }, [getStemIcon]);

  // Function to prepare track data for multitrack player (Table 2)
  const prepareTracksForTable2Model = useCallback((selectedExample, modelConfig, exampleMetadata) => {
    if (!exampleMetadata || !exampleMetadata.allStems) return [];

    const tracks = exampleMetadata.allStems.map((stem, stemIndex) => {
      const audioPath = getTable2AudioPath(selectedExample, modelConfig, stemIndex);
      return {
        src: audioPath,
        name: stem,
        icon: getStemIcon(stem),
        stemType: stem
      };
    });

    return tracks;
  }, [getStemIcon]);

  // Function to prepare track data for multistem player (Composer - 2nd Pass with 1st Pass Mix)
  const prepareTracksForComposer2ndPass = useCallback((selectedComposerExample, selectedVariation, composerExampleMetadata) => {
    if (!composerExampleMetadata || !composerExampleMetadata.stems) return [];

    const currentVariation = getCurrentVariation(selectedComposerExample, selectedVariation);
    
    // Add all 2nd pass stems
    const stemTracks = composerExampleMetadata.stems.map((stem) => ({
      src: getComposerAudioPath(currentVariation, stem.type),
      name: stem.type.replace(/_/g, ' '),
      icon: getStemIcon(stem.type.replace(/_/g, ' ')),
      stemType: stem.type
    }));

    // Add 1st pass mix as the last "stem" for comparison
    const firstPassMixTrack = {
      src: `${process.env.PUBLIC_URL}/audio/${selectedComposerExample.baseFolder}/output_mix.mp3`,
      name: '1st Pass Mix',
      icon: '🎵',
      stemType: '1st_pass_mix'
    };

    return [...stemTracks, firstPassMixTrack];
  }, [getStemIcon]);

  // Function to prepare track data for multistem player (Composer - 1st Pass)  
  const prepareTracksForComposer1stPass = useCallback((selectedComposerExample) => {
    const stems = selectedComposerExample.pass1Metadata?.output_stems || [];
    const tracks = stems.map((stemType) => ({
      src: `${process.env.PUBLIC_URL}/audio/${selectedComposerExample.baseFolder}/${stemType}/output.mp3`,
      name: stemType.replace(/_/g, ' '),
      icon: getStemIcon(stemType.replace(/_/g, ' ')),
      stemType
    }));

    return tracks;
  }, [getStemIcon]);

  const subpages = [
    { id: 'main-ablation', title: 'Main Ablation', subtitle: 'Table 1' },
    { id: 'workflow-improvement', title: 'Workflow Improvement', subtitle: 'Table 2' },
    { id: 'composer-generations', title: 'Composer-like Generations', subtitle: 'Real-world scenarios' }
  ];

  // Load metadata when example changes
  useEffect(() => {
    if (selectedExample) {
      try {
        console.log('Loading metadata for example:', selectedExample.name);
        const metadata = loadExampleMetadata(selectedExample);
        console.log('Metadata loaded successfully:', metadata ? 'Yes' : 'No');
        setExampleMetadata(metadata);
      } catch (error) {
        console.error('Failed to load metadata in useEffect:', error);
        setExampleMetadata(null);
      }
    }
  }, [selectedExample]);

  // Load Table 2 metadata when example changes
  useEffect(() => {
    if (selectedTable2Example) {
      try {
        console.log('Loading Table 2 metadata for example:', selectedTable2Example.name);
        const metadata = loadTable2ExampleMetadata(selectedTable2Example);
        console.log('Table 2 metadata loaded successfully:', metadata ? 'Yes' : 'No');
        setTable2ExampleMetadata(metadata);

        // Load generation times for all models (now synchronous)
        const times = {};
        for (const modelConfig of table2ModelConfigs) {
          const time = getGenerationTime(selectedTable2Example, modelConfig);
          times[modelConfig.id] = time;
        }
        setGenerationTimes(times);
      } catch (error) {
        console.error('Failed to load Table 2 metadata in useEffect:', error);
        setTable2ExampleMetadata(null);
      }
    }
  }, [selectedTable2Example]);

  // Load Composer metadata when example or variation changes
  useEffect(() => {
    if (selectedComposerExample) {
      const loadMetadata = async () => {
        try {
          console.log('Loading Composer metadata for example:', selectedComposerExample.id, 'variation:', selectedVariation);
          const currentVariation = getCurrentVariation(selectedComposerExample, selectedVariation);
          
          // Load metadata dynamically if not already loaded
          await loadVariationMetadata(currentVariation);
          
          const metadata = loadComposerExampleMetadata(currentVariation);
          console.log('Composer metadata loaded successfully:', metadata ? 'Yes' : 'No');
          setComposerExampleMetadata(metadata);
          // Reset hover and pin states when example or variation changes
          isHoveringRef.current = false;
          setHoveredVisualization(null);
          setPinnedVisualization(null);
        } catch (error) {
          console.error('Failed to load Composer metadata in useEffect:', error);
          setComposerExampleMetadata(null);
        }
      };
      
      loadMetadata();
    }
  }, [selectedComposerExample, selectedVariation]);

  // Reset variation to 'a' when example changes
  useEffect(() => {
    setSelectedVariation('a');
  }, [selectedComposerExample]);

  const renderTable1Content = () => {
    return (
      <div className="subpage-content">
        <div className="table1-container">
          {/* Experiment Description */}
          <div className="experiment-description">
            <p>
              This ablation study examines our core techniques: <strong>data grouping</strong> and <strong>noise sharing</strong>.
              We evaluate models trained & inferenced with different combinations of these techniques, generating all stems in a <strong>single inference pass</strong>.
              You can compare generation quality over 10 examples from MoisesDB and MusDB datasets.
            </p>
          </div>

          {/* Example Selector */}
          <div className="example-selector">
            <label className="selector-label">Select Example</label>
            <div className="example-chips-container">
              {/* MoisesDB Row */}
              <div className="dataset-row">
                <div className="example-chips">
                  {table1Examples.filter(ex => ex.dataset === 'MoisesDB').map((example) => (
                    <button
                      key={example.id}
                      className={`example-chip ${selectedExample?.id === example.id ? 'selected' : ''}`}
                      onClick={() => setSelectedExample(example)}
                    >
                      <span className="dataset-badge">{example.dataset}</span>
                      <span className="example-name">{example.displayName}</span>
                    </button>
                  ))}
                </div>
              </div>
              {/* MusDB Row */}
              <div className="dataset-row">
                <div className="example-chips">
                  {table1Examples.filter(ex => ex.dataset === 'MusDB').map((example) => (
                    <button
                      key={example.id}
                      className={`example-chip ${selectedExample?.id === example.id ? 'selected' : ''}`}
                      onClick={() => setSelectedExample(example)}
                    >
                      <span className="dataset-badge">{example.dataset}</span>
                      <span className="example-name">{example.displayName}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Global Prompt Display */}
          {exampleMetadata && (
            <div className="prompt-display">
              <label className="prompt-label">Global Text Prompt</label>
              <div className="prompt-box">
                {exampleMetadata.caption_orig}
              </div>
            </div>
          )}

          {/* Model Generations Section */}
          {exampleMetadata && (
            <div className="model-generations-section">
              <div className="section-header-with-toggle">
                <label className="section-label">Model Generations</label>
                <button 
                  className={`player-mode-toggle ${useMultitrackPlayer ? 'multistem' : 'basic'}`}
                  onClick={() => setUseMultitrackPlayer(!useMultitrackPlayer)}
                  title={`Switch to ${useMultitrackPlayer ? 'Basic' : 'Pro Multistem'} Player`}
                >
                  {useMultitrackPlayer ? (
                    <><span className="toggle-icon">🎵</span> Use Basic Player</>
                  ) : (
                    <><span className="toggle-icon">🎛️</span> Use Pro Multistem Player</>
                  )}
                </button>
              </div>

              {/* Conditional rendering based on player mode */}
              {useMultitrackPlayer ? (
                /* Multistem Player Mode */
                <div className="multitrack-players-section">
                  <div className="multitrack-players-container">
                    {modelConfigs.map((modelConfig) => (
                      <MultitrackPlayerWrapper
                        key={modelConfig.id}
                        modelConfig={modelConfig}
                        selectedExample={selectedExample}
                        exampleMetadata={exampleMetadata}
                        prepareTracksForModel={prepareTracksForModel}
                        getStemIcon={getStemIcon}
                        generationTimes={generationTimes}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                /* Basic Audio Player Mode */
                <div className="model-comparison-grid">
                  <div className="model-headers">
                    <div className="stem-labels-column">
                      <div className="header-cell">Setting</div>
                    </div>
                    {modelConfigs.map((model) => (
                      <div key={model.id} className="model-column">
                        <div className="model-header">
                          <div className="model-name">
                            {model.displayName}
                            {model.displayName === 'C-(ii)' && <span className="best-indicator">(Our Best)</span>}
                            {model.displayName === 'A-(i)' && <span className="best-indicator">(Prior Art)</span>}
                          </div>
                          <div className="model-settings">
                            <span className="setting-item">
                              <span className="setting-label">data group:</span>
                              <span className={`setting-value ${model.id.startsWith('model_A') ? 'disabled' : 'enabled'}`}>
                                {model.id.startsWith('model_A') ? '✗' : '✓'}
                              </span>
                            </span>
                            <span className="setting-item">
                              <span className="setting-label">noise share (train):</span>
                              <span className={`setting-value ${model.id === 'model_C_ii' ? 'enabled' : 'disabled'}`}>
                                {model.id === 'model_C_ii' ? '✓' : '✗'}
                              </span>
                            </span>
                            <span className="setting-item">
                              <span className="setting-label">noise share (infer):</span>
                              <span className={`setting-value ${(model.id === 'model_A_ii' || model.id === 'model_B_ii' || model.id === 'model_C_ii') ? 'enabled' : 'disabled'}`}>
                                {(model.id === 'model_A_ii' || model.id === 'model_B_ii' || model.id === 'model_C_ii') ? '✓' : '✗'}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Mix Row */}
                  <div className="audio-section mix-section">
                    <div className="audio-row mix-row">
                      <div className="stem-label-cell">
                        <span className="stem-name">Full Mix</span>
                      </div>
                      {modelConfigs.map((model) => (
                        <div key={`${model.id}-mix`} className="audio-cell" data-model={model.displayName}>
                          <audio
                            key={`${selectedExample.id}-${model.id}-mix`}
                            controls
                            controlsList="nodownload"
                            preload="metadata"
                            className="audio-player"
                            src={getAudioPath(selectedExample, model.id)}
                          >
                            Your browser does not support audio playback.
                          </audio>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Individual Stem Rows */}
                  <div className="stems-label-section">
                    <label className="section-label">Constituent Stems</label>
                  </div>
                  <div className="audio-section stems-section">
                    {exampleMetadata.orderedStems.map((stem) => (
                      <div key={stem.folder} className="audio-row stem-row">
                        <div className="stem-label-cell">
                          <span className="stem-icon">{getStemIcon(stem.stemType)}</span>
                          <span className="stem-name">{stem.stemType}</span>
                        </div>
                        {modelConfigs.map((model) => (
                          <div key={`${model.id}-${stem.folder}`} className="audio-cell" data-model={model.displayName}>
                            <audio
                              key={`${selectedExample.id}-${model.id}-stem-${stem.folder}`}
                              controls
                              controlsList="nodownload"
                              preload="metadata"
                              className="audio-player"
                              src={getAudioPath(selectedExample, model.id, stem.folder)}
                            >
                              Your browser does not support audio playback.
                            </audio>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!exampleMetadata && (
            <div className="loading-message">
              <p>Loading example data...</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTable2Content = () => {
    return (
      <div className="subpage-content">
        <div className="table1-container">
          {/* Experiment Description */}
          <div className="experiment-description table2-description">
            <p>
              This experiment compares workflows for creating coherent multi-stem music.
              Select examples below to hear how the '<strong>C-(ii)</strong>' setting performs with efficient <strong>1-pass</strong> and <strong>2-pass</strong> workflows,
              compared to the baseline <strong>K-pass</strong> workflow (for K stems) possible with existing models, i.e., '<strong>A-(i)</strong>'.
            </p>
          </div>

          {/* Example Selector */}
          <div className="example-selector">
            <label className="selector-label">Select Example</label>
            <div className="example-chips-container">
              {/* MoisesDB Row */}
              <div className="dataset-row">
                <div className="example-chips">
                  {table2Examples.filter(ex => ex.dataset === 'MoisesDB').map((example) => (
                    <button
                      key={example.id}
                      className={`example-chip ${selectedTable2Example?.id === example.id ? 'selected' : ''}`}
                      onClick={() => setSelectedTable2Example(example)}
                    >
                      <span className="dataset-badge">{example.dataset}</span>
                      <span className="example-name">{example.displayName}</span>
                    </button>
                  ))}
                </div>
              </div>
              {/* MusDB Row */}
              <div className="dataset-row">
                <div className="example-chips">
                  {table2Examples.filter(ex => ex.dataset === 'MusDB').map((example) => (
                    <button
                      key={example.id}
                      className={`example-chip ${selectedTable2Example?.id === example.id ? 'selected' : ''}`}
                      onClick={() => setSelectedTable2Example(example)}
                    >
                      <span className="dataset-badge">{example.dataset}</span>
                      <span className="example-name">{example.displayName}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Global Text Prompt */}
          {table2ExampleMetadata && (
            <div className="prompt-display">
              <div className="prompt-label">Global Text Prompt</div>
              <div className="prompt-box">
                {table2ExampleMetadata.caption}
              </div>
            </div>
          )}

          {/* Model Generations Section */}
          {table2ExampleMetadata && (
            <div className="model-generations-section">
              <div className="section-header-with-toggle">
                <label className="section-label">Model Generations</label>
                <button 
                  className={`player-mode-toggle ${useMultitrackPlayer ? 'multistem' : 'basic'}`}
                  onClick={() => setUseMultitrackPlayer(!useMultitrackPlayer)}
                  title={`Switch to ${useMultitrackPlayer ? 'Basic' : 'Pro Multistem'} Player`}
                >
                  {useMultitrackPlayer ? (
                    <><span className="toggle-icon">🎵</span> Use Basic Player</>
                  ) : (
                    <><span className="toggle-icon">🎛️</span> Use Pro Multistem Player</>
                  )}
                </button>
              </div>

              {/* Conditional rendering based on player mode */}
              {useMultitrackPlayer ? (
                /* Multistem Player Mode */
                <div className="multitrack-players-section">
                  <div className="table2-multitrack-players">
                    {table2ModelConfigs.map((model) => (
                      <div key={model.id} className="table2-model-block">
                        <Table2MultitrackPlayerWrapper
                          modelConfig={model}
                          selectedExample={selectedTable2Example}
                          exampleMetadata={table2ExampleMetadata}
                          prepareTracksForTable2Model={prepareTracksForTable2Model}
                          getStemIcon={getStemIcon}
                          generationTimes={generationTimes}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                /* Basic Audio Player Mode */
                <div className="model-comparison-grid">
                  {/* Headers */}
                  <div className="model-headers">
                    <div className="stem-labels-column">
                      <div className="header-cell">Setting</div>
                    </div>
                    {table2ModelConfigs.map((model) => (
                      <div key={model.id} className="model-column">
                        <div className="model-header">
                          <div className="model-name">
                            {model.name}
                            {model.name === 'C-(ii)' && model.description === '2 Pass' && <span className="best-indicator">(Our Best)</span>}
                            {model.name === 'A-(i)' && <span className="best-indicator">(Prior Art)</span>}
                          </div>
                          <div className="model-description">{model.description}</div>
                          {/* Generation Time Display */}
                          <div className="generation-time">
                            {generationTimes[model.id] !== undefined && generationTimes[model.id] !== null ? (
                              <span className="time-display">
                                Inference ⏱️  &nbsp; {generationTimes[model.id].toFixed(1)}s
                              </span>
                            ) : (
                              <span className="time-display loading">⏱️ Loading...</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Full Mix Audio Section */}
                  <div className="audio-section mix-section">
                    <div className="audio-row mix-row">
                      <div className="stem-labels-column">
                        <div className="stem-label-cell">
                          <span className="stem-name">Full Mix</span>
                        </div>
                      </div>
                      {table2ModelConfigs.map((model) => (
                        <div key={`${model.id}-mix`} className="model-column">
                          <audio
                            key={`${selectedTable2Example.id}-${model.id}-mix`}
                            controls
                            className="audio-player"
                            controlsList="nodownload"
                            preload="metadata"
                          >
                            <source
                              src={getTable2AudioPath(selectedTable2Example, model)}
                              type="audio/mpeg"
                            />
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Constituent Stems Label */}
                  <div className="stems-label-section">
                    <div className="section-label">Constituent Stems</div>
                  </div>

                  {/* Individual Stems Audio Section */}
                  <div className="audio-section stems-section">
                    {table2ExampleMetadata.allStems.map((stem, stemIndex) => (
                      <div key={`stem-${stemIndex}`} className="audio-row">
                        <div className="stem-labels-column">
                          <div className="stem-label-cell">
                            <span className="stem-icon">{getStemIcon(stem)}</span>
                            <span className="stem-name">{stem}</span>
                          </div>
                        </div>
                        {table2ModelConfigs.map((model) => (
                          <div key={`${model.id}-stem-${stemIndex}`} className="model-column">
                            <audio
                              key={`${selectedTable2Example.id}-${model.id}-stem-${stemIndex}`}
                              controls
                              className="audio-player"
                              controlsList="nodownload"
                              preload="metadata"
                            >
                              <source
                                src={getTable2AudioPath(selectedTable2Example, model, stemIndex)}
                                type="audio/mpeg"
                              />
                              Your browser does not support the audio element.
                            </audio>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderComposerContent = () => {
    return (
      <div className="subpage-content">
        <div className="table3-container">
          {/* Experiment Description */}
          <div className="experiment-description table3-description">
            <p>
              Here, we imagine ourselves as composers and come up with text prompts, constituent stem types, and activity controls to generate a composition.
              The model used is <strong>C-(ii) + Activity Control</strong> with the <strong>2-pass</strong> workflow.
              We iterate on the prompts, stems, and activity controls to (re)generate until we are satisfied.
              {/* This experiment showcases <strong>composer-like generation</strong> capabilities.
              Select examples below to hear full multi-stem compositions generated with our unified model.
              Each example demonstrates the model's ability to create coherent, stylistically diverse musical pieces across different genres. */}
            </p>
          </div>

          {/* Example Selector */}
          <div className="example-selector">
            <label className="selector-label">Select Example</label>
            <div className="example-chips-container">
              <div className="dataset-row">
                <div className="example-chips">
                  {composerExamples.map((example) => (
                    <button
                      key={example.id}
                      className={`example-chip ${selectedComposerExample?.id === example.id ? 'selected' : ''}`}
                      onClick={() => setSelectedComposerExample(example)}
                    >
                      <span className="dataset-badge">{example.style}</span>
                      <span className="example-name">{example.displayName}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Variation Selector */}
          {selectedComposerExample && selectedComposerExample.variations.length > 0 && (
            <div className="example-selector">
              <label className="selector-label">Select Variation <span className="selector-supplement">(all variations share the same 1st pass & style, but have different 2nd passes and different final full mixes)</span></label>
              <div className="example-chips-container">
                <div className="dataset-row">
                  <div className="example-chips">
                    {selectedComposerExample.variations.map((variation) => (
                      <button
                        key={variation.id}
                        className={`example-chip ${selectedVariation === variation.id ? 'selected' : ''}`}
                        onClick={() => setSelectedVariation(variation.id)}
                      >
                        <span className="example-name">{variation.displayName}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Global Text Prompt */}
          {composerExampleMetadata && (
            <div className="prompt-display">
              <div className="prompt-header">
                <label className="prompt-label">
                  Global Text Prompt
                  <div className="prompt-toggle-group">
                    <button
                      className={`prompt-toggle-btn left ${!showPass1Prompt ? 'active' : ''}`}
                      onClick={() => setShowPass1Prompt(false)}
                    >
                      2nd (final) pass
                    </button>
                    <button
                      className={`prompt-toggle-btn right ${showPass1Prompt ? 'active' : ''}`}
                      onClick={() => setShowPass1Prompt(true)}
                    >
                      1st pass
                    </button>
                  </div>
                </label>
              </div>
              <div className="prompt-box">
                {showPass1Prompt
                  ? selectedComposerExample.pass1Metadata?.prompt || 'No 1st pass prompt available'
                  : composerExampleMetadata.prompt
                }
              </div>
            </div>
          )}

          {/* Model Generations */}
          {composerExampleMetadata && (
            <div className="model-generations-section">
              <div className="section-header-with-toggle">
                <label className="section-label">Model Generations</label>
                <button 
                  className={`player-mode-toggle ${useMultitrackPlayer ? 'multistem' : 'basic'}`}
                  onClick={() => setUseMultitrackPlayer(!useMultitrackPlayer)}
                  title={`Switch to ${useMultitrackPlayer ? 'Basic' : 'Pro Multistem'} Player`}
                >
                  {useMultitrackPlayer ? (
                    <><span className="toggle-icon">🎵</span> Use Basic Player</>
                  ) : (
                    <><span className="toggle-icon">🎛️</span> Use Pro Multistem Player</>
                  )}
                </button>
              </div>

              {/* Activity Control Instructions - Different for Pro vs Basic mode */}
              <div className="experiment-description table3-description activity-control-instructions">
                <p>
                  {useMultitrackPlayer ? (
                    // Pro Multistem Player Mode - Activity curves shown below players
                    <span className="text-content">
                      Activity control visualizations are shown below each multitrack player. They display the <strong>activity control input</strong> and the resulting <strong>output audio spectrogram</strong>.
                    </span>
                  ) : (
                    // Basic Player Mode - Wave icon interaction
                    <>
                      <span className="wave-icon-inline">
                        <svg className="wave-icon" viewBox="0 0 20 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 9 L5 9 L5 3 L15 3 L15 9 L19 9"/>
                        </svg>
                      </span>
                      <span className="text-content">
                        "Hover over (to peek) / Click (to pin)" the wave icon next to individual stems to visualize <strong>activity controls</strong>.
                        The visualization has two subplots: the first displays the <strong>activity control input (user-specified)</strong>, and the second shows the <strong>output audio spectrogram</strong>.
                      </span>
                    </>
                  )}
                </p>
              </div>

              {/* Conditional rendering based on player mode */}
              {useMultitrackPlayer ? (
                /* Multistem Player Mode */
                <div className="multitrack-players-section">
                  <div className="composer-multitrack-players">
                    {/* Full Mix Player */}
                    <div className="composer-mix-section">
                      <label className="section-label">Full Mix</label>
                      <div className="multitrack-player-wrapper">
                        <MultitrackPlayer
                          key={`${selectedComposerExample.id}-${selectedVariation}-full-mix`}
                          tracks={[{
                            src: getComposerAudioPath(getCurrentVariation(selectedComposerExample, selectedVariation), 'mix'),
                            name: 'Full Mix',
                            icon: '🎵',
                            stemType: 'mix'
                          }]}
                          modelConfig={{ workflow: true, name: 'C-(ii) + Act Ctrl', description: '2 Pass', displayName: 'C-(ii) + Act Ctrl' }}
                          selectedExample={{ id: `${selectedComposerExample.id}-${selectedVariation}-full-mix` }}
                          getStemIcon={getStemIcon}
                          hideHeader={true}
                        />
                      </div>
                    </div>

                    {/* 2nd Pass Stems Player (with 1st Pass Mix included) */}
                    <div className="composer-stems-section">
                      <label className="section-label">Constituent Stems (2nd Pass)</label>
                      <div className="multitrack-player-wrapper">
                        <MultitrackPlayer
                          key={`${selectedComposerExample.id}-${selectedVariation}-pass2-stems`}
                          tracks={prepareTracksForComposer2ndPass(selectedComposerExample, selectedVariation, composerExampleMetadata)}
                          modelConfig={{ workflow: true, name: 'C-(ii) + Act Ctrl', description: '2 Pass', displayName: 'C-(ii) + Act Ctrl' }}
                          selectedExample={{ id: `${selectedComposerExample.id}-${selectedVariation}-pass2` }}
                          getStemIcon={getStemIcon}
                          hideHeader={true}
                        />
                      </div>

                      {/* Activity curves below player, side-by-side */}
                      <div className="table3-activity-curves">
                        {composerExampleMetadata.stems.map((stem) => (
                          <div key={`curve-pass2-${stem.type}`} className="curve-card">
                            <div className="curve-title">{stem.type.replace(/_/g, ' ')}</div>
                            <img
                              className="curve-image"
                              src={getVisualizationPath(selectedComposerExample, stem.type)}
                              alt={`Activity control visualization for ${stem.type.replace(/_/g, ' ')}`}
                              onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'block'; }}
                            />
                            <div className="curve-missing" style={{display: 'none'}}>Visualization not available</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* 1st Pass Stems Player */}
                    <div className="composer-stems-section">
                      <label className="section-label">Constituent Stems (1st Pass)</label>
                      <div className="multitrack-player-wrapper">
                        <MultitrackPlayer
                          key={`${selectedComposerExample.id}-${selectedVariation}-pass1-stems`}
                          tracks={prepareTracksForComposer1stPass(selectedComposerExample)}
                          modelConfig={{ workflow: true, name: 'C-(ii) + Act Ctrl', description: '2 Pass', displayName: 'C-(ii) + Act Ctrl' }}
                          selectedExample={{ id: `${selectedComposerExample.id}-${selectedVariation}-pass1` }}
                          getStemIcon={getStemIcon}
                          hideHeader={true}
                        />
                      </div>

                      {/* Activity curves below player, side-by-side */}
                      <div className="table3-activity-curves">
                        {(selectedComposerExample.pass1Metadata?.output_stems || []).map((stemType) => (
                          <div key={`curve-pass1-${stemType}`} className="curve-card">
                            <div className="curve-title">{stemType.replace(/_/g, ' ')}</div>
                            <img
                              className="curve-image"
                              src={getPass1VisualizationPath(selectedComposerExample, stemType)}
                              alt={`Activity control visualization for ${stemType.replace(/_/g, ' ')}`}
                              onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'block'; }}
                            />
                            <div className="curve-missing" style={{display: 'none'}}>Visualization not available</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Basic Audio Player Mode */
                <div className="model-comparison-grid">
                  {/* Headers */}
                  <div className="model-headers">
                    <div className="stem-labels-column">
                      <div className="header-cell">Setting</div>
                    </div>
                    <div className="model-column">
                      <div className="model-header">
                        <div className="model-name">C-(ii) + Act Ctrl</div>
                        <div className="model-workflow">2 Pass</div>
                      </div>
                    </div>
                  </div>

                  {/* Full Mix Audio Section */}
                  <div className="audio-section mix-section">
                    <div className="audio-row mix-row">
                      <div className="stem-labels-column">
                        <div className="stem-label-cell">
                          <span className="stem-name">Full Mix</span>
                        </div>
                      </div>
                      <div className="model-column">
                        <audio
                          key={`${selectedComposerExample.id}-mix`}
                          controls
                          controlsList="nodownload"
                          preload="metadata"
                          className="audio-player"
                          src={getComposerAudioPath(getCurrentVariation(selectedComposerExample, selectedVariation), 'mix')}
                        >
                          Your browser does not support audio playback.
                        </audio>
                      </div>
                    </div>
                  </div>

                {/* Constituent Stems (2nd Pass) Label */}
                <div className="stems-label-section">
                  <div className="section-label">Constituent Stems (2nd Pass)</div>
                </div>

                {/* 2nd Pass Stems Audio Section */}
                <div className="audio-section stems-section">
                  {composerExampleMetadata.stems.map((stem, stemIndex) => (
                    <div key={`pass2-${stem.type}`} className="audio-row">
                      <div className="stem-labels-column">
                        <div className="stem-label-cell">
                          <span className="stem-icon">{getStemIcon(stem.type.replace(/_/g, ' '))}</span>
                          <span className="stem-name">{stem.type.replace(/_/g, ' ')}</span>
                        </div>
                      </div>
                      <div className="model-column">
                        <div className="audio-with-controls">
                          <audio
                            key={`${selectedComposerExample.id}-pass2-${stem.folder}`}
                            controls
                            controlsList="nodownload"
                            preload="metadata"
                            className="audio-player"
                            src={getComposerAudioPath(getCurrentVariation(selectedComposerExample, selectedVariation), stem.type)}
                          >
                            Your browser does not support audio playback.
                          </audio>
                          <div className="wave-icon-container">
                            <button
                              className={`wave-icon-btn ${hasActivityControl(selectedComposerExample, stem.type) ? 'enabled' : 'disabled'} ${pinnedVisualization === `pass2-${stem.type}` ? 'pinned' : ''}`}
                              onMouseEnter={() => hasActivityControl(selectedComposerExample, stem.type) ? handleWaveHover(stem.type) : handleDisabledHover(stem.type)}
                              onMouseLeave={() => hasActivityControl(selectedComposerExample, stem.type) ? handleWaveLeave() : handleDisabledLeave()}
                              onClick={() => hasActivityControl(selectedComposerExample, stem.type) && handleWaveClick(stem.type)}
                              title={hasActivityControl(selectedComposerExample, stem.type) 
                                ? (pinnedVisualization === `pass2-${stem.type}` 
                                  ? "Unpin activity control visualization" 
                                  : "Pin activity control visualization")
                                : "Activity control not in use"}
                            >
                              <svg className="wave-icon" viewBox="0 0 20 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 9 L5 9 L5 3 L15 3 L15 9 L19 9"/>
                              </svg>
                            </button>
                            {(hoveredVisualization === `pass2-${stem.type}` || pinnedVisualization === `pass2-${stem.type}`) && (
                              <div 
                                className="visualization-overlay"
                                onClick={(e) => {
                                  if (e.target.className === 'visualization-overlay') {
                                    setPinnedVisualization(null);
                                    setHoveredVisualization(null);
                                  }
                                }}
                              >
                                <div className="visualization-content">
                                <div className="visualization-header">
                                  <span className="visualization-title">Activity Control - {stem.type.replace(/_/g, ' ')}</span>
                                  <button 
                                    className={`close-visualization ${pinnedVisualization ? 'visible' : 'invisible'}`}
                                    onClick={() => setPinnedVisualization(null)}
                                    title="Close visualization"
                                  >
                                    ×
                                  </button>
                                </div>
                                  <img
                                    src={getVisualizationPath(selectedComposerExample, stem.type)}
                                    alt={`Activity control visualization for ${stem.type.replace(/_/g, ' ')}`}
                                    className="visualization-image"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'block';
                                    }}
                                  />
                                  <div className="visualization-error" style={{display: 'none'}}>
                                    Visualization not available
                                  </div>
                                </div>
                              </div>
                            )}
                            {hoveredDisabled === `pass2-${stem.type}` && (
                              <div className="visualization-overlay">
                                <div className="visualization-content disabled-message">
                                  <div className="visualization-header">
                                    <span className="visualization-title">Activity control not in use</span>
                                  </div>
                                  <div className="disabled-message-content">
                                    <p>This stem was generated without activity control.</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Subtle Divider */}
                <div className="section-divider"></div>

                {/* 1st Pass Mix Audio Section */}
                <div className="audio-section mix-section">
                  <div className="audio-row mix-row first-pass-mix-row">
                    <div className="stem-labels-column">
                      <div className="stem-label-cell">
                        <span className="stem-name">1st-Pass Mix</span>
                      </div>
                    </div>
                    <div className="model-column">
                      <audio
                        key={`${selectedComposerExample.id}-pass1-mix`}
                        controls
                        controlsList="nodownload"
                        preload="metadata"
                        className="audio-player"
                        src={`${process.env.PUBLIC_URL}/audio/${selectedComposerExample.baseFolder}/output_mix.mp3`}
                      >
                        Your browser does not support audio playback.
                      </audio>
                    </div>
                  </div>
                </div>

                {/* Constituent Stems (1st Pass) Label */}
                <div className="stems-label-section">
                  <div className="section-label">Constituent Stems (1st Pass)</div>
                </div>

                {/* 1st Pass Stems Audio Section */}
                <div className="audio-section stems-section">
                  {selectedComposerExample.pass1Metadata?.output_stems?.map((stemType, stemIndex) => (
                    <div key={`pass1-${stemType}`} className="audio-row">
                      <div className="stem-labels-column">
                        <div className="stem-label-cell">
                          <span className="stem-icon">{getStemIcon(stemType.replace(/_/g, ' '))}</span>
                          <span className="stem-name">{stemType.replace(/_/g, ' ')}</span>
                        </div>
                      </div>
                      <div className="model-column">
                        <div className="audio-with-controls">
                          <audio
                            key={`${selectedComposerExample.id}-pass1-${stemType}`}
                            controls
                            controlsList="nodownload"
                            preload="metadata"
                            className="audio-player"
                            src={`${process.env.PUBLIC_URL}/audio/${selectedComposerExample.baseFolder}/${stemType}/output.mp3`}
                          >
                            Your browser does not support audio playback.
                          </audio>
                          <div className="wave-icon-container">
                            <button
                              className={`wave-icon-btn ${hasPass1ActivityControl(selectedComposerExample, stemType) ? 'enabled' : 'disabled'} ${pinnedVisualization === `pass1-${stemType}` ? 'pinned' : ''}`}
                              onMouseEnter={() => hasPass1ActivityControl(selectedComposerExample, stemType) ? handleWaveHover(stemType, 'pass1') : handleDisabledHover(stemType, 'pass1')}
                              onMouseLeave={() => hasPass1ActivityControl(selectedComposerExample, stemType) ? handleWaveLeave() : handleDisabledLeave()}
                              onClick={() => hasPass1ActivityControl(selectedComposerExample, stemType) && handleWaveClick(stemType, 'pass1')}
                              title={hasPass1ActivityControl(selectedComposerExample, stemType) 
                                ? (pinnedVisualization === `pass1-${stemType}` 
                                  ? "Unpin activity control visualization" 
                                  : "Pin activity control visualization")
                                : "Activity control not in use"}
                            >
                              <svg className="wave-icon" viewBox="0 0 20 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 9 L5 9 L5 3 L15 3 L15 9 L19 9"/>
                              </svg>
                            </button>
                            {(hoveredVisualization === `pass1-${stemType}` || pinnedVisualization === `pass1-${stemType}`) && (
                              <div 
                                className="visualization-overlay"
                                onClick={(e) => {
                                  if (e.target.className === 'visualization-overlay') {
                                    setPinnedVisualization(null);
                                    setHoveredVisualization(null);
                                  }
                                }}
                              >
                                <div className="visualization-content">
                                <div className="visualization-header">
                                  <span className="visualization-title">Activity Control - {stemType.replace(/_/g, ' ')}</span>
                                  <button 
                                    className={`close-visualization ${pinnedVisualization ? 'visible' : 'invisible'}`}
                                    onClick={() => setPinnedVisualization(null)}
                                    title="Close visualization"
                                  >
                                    ×
                                  </button>
                                </div>
                                  <img
                                    src={getPass1VisualizationPath(selectedComposerExample, stemType)}
                                    alt={`Activity control visualization for ${stemType.replace(/_/g, ' ')}`}
                                    className="visualization-image"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'block';
                                    }}
                                  />
                                  <div className="visualization-error" style={{display: 'none'}}>
                                    Visualization not available
                                  </div>
                                </div>
                              </div>
                            )}
                            {hoveredDisabled === `pass1-${stemType}` && (
                              <div className="visualization-overlay">
                                <div className="visualization-content disabled-message">
                                  <div className="visualization-header">
                                    <span className="visualization-title">Activity control not in use</span>
                                  </div>
                                  <div className="disabled-message-content">
                                    <p>This stem was generated without activity control.</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSubpageContent = () => {
    switch (activeSubpage) {
      case 'main-ablation':
        return renderTable1Content();
      case 'workflow-improvement':
        return renderTable2Content();
      case 'composer-generations':
        return renderComposerContent();
      default:
        return null;
    }
  };

  return (
    <div className="App">
      <div className="container">
        <div className="paper-header">
          <h1 className="paper-title">
            <div className="title-main">Stemphonic</div>
            <div className="title-sub">All-at-once Flexible Multi-stem Music Generation</div>
            <div className="title-acceptance">Accepted to ICASSP 2026</div>
          </h1>
          <div className="authors-row">
            <span className="author">Shih-Lun Wu<sup>♯♭<span className="asterisk-subdued">*</span></sup></span>
            <span className="author">Ge Zhu<sup>♭</sup></span>
            <span className="author">Juan-Pablo Caceres<sup>♭</sup></span>
            <span className="author">Cheng-Zhi Anna Huang<sup>♯</sup></span>
            <span className="author">Nicholas J. Bryan<sup>♭</sup></span>
          </div>

          <div className="affiliations">
            <div className="affiliation">
              <sup>♯</sup>
              <img src={`${process.env.PUBLIC_URL}/logos/CSAIL_Primary_Regular_RGB.svg`} alt="MIT CSAIL" className="affiliation-logo" />
              <span className="affiliation-text">MIT CSAIL</span>
            </div>
            <div className="affiliation">
              <sup>♭</sup>
              <img src={`${process.env.PUBLIC_URL}/logos/Adobe_Corporate_logo.svg`} alt="Adobe Research" className="affiliation-logo" />
              <span className="affiliation-text">Adobe Research</span>
            </div>
          </div>

          <div className="internship-note">
            * Work done while an intern at Adobe Research
          </div>

          {/* <div className="conference-info">
            <span className="conference">ICASSP 2026</span>
            <span className="separator">•</span>
            <span className="venue">Seoul, South Korea</span>
          </div> */}

          {/* Abstract and Bibtex Buttons */}
          <div className="paper-action-buttons">
            <a 
              href="https://www.youtube.com/watch?v=IrGD3CHaPYU" 
              target="_blank" 
              rel="noopener noreferrer"
              className="paper-action-btn"
            >
              <i className="fab fa-youtube"></i>
              <span>Video</span>
            </a>
            <button className="paper-action-btn" onClick={scrollToSoundExamples}>
              <i className="fas fa-music"></i>
              <span>Sound Examples</span>
            </button>
            <button className="paper-action-btn" onClick={toggleAbstract}>
              <i className="fas fa-align-left"></i>
              <span>Abstract</span>
            </button>
            <a 
              href="https://arxiv.org/abs/2602.09891" 
              target="_blank" 
              rel="noopener noreferrer"
              className="paper-action-btn"
            >
              <i className="fas fa-file-alt"></i>
              <span>Paper</span>
            </a>
            <button className="paper-action-btn" onClick={scrollToBibtex}>
              <i className="fas fa-quote-left"></i>
              <span>Bibtex</span>
            </button>
          </div>

          {/* Collapsible Abstract */}
          {showAbstract && (
            <div className="abstract-section">
              <div className="abstract-content">
                {abstractText}
              </div>
            </div>
          )}

          {/* Suggestion Block */}
          <div className="suggestion-block">
            Use <strong>Chrome desktop</strong><i className="fab fa-chrome chrome-icon"></i> browser and listen loud with <strong>headphones</strong><span className="headphones-icon">🎧</span> for best experience
          </div>
        </div>

        {/* Intro Video Section */}
        <div className="intro-video-section">
          <h2 className="intro-video-title">Intro Video (2 min)</h2>
          <div className="video-wrapper">
            <div className="video-container">
              <iframe
                src="https://www.youtube.com/embed/IrGD3CHaPYU"
                title="Stemphonic Intro Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="video-iframe"
              ></iframe>
            </div>
          </div>
        </div>

        {/* Subpage Navigation */}
        <div className="subpage-navigation" ref={soundExamplesRef}>
          <div className="subpage-toggle-buttons">
            {subpages.map((subpage) => (
              <button
                key={subpage.id}
                className={`subpage-toggle-btn ${activeSubpage === subpage.id ? 'active' : ''}`}
                onClick={() => setActiveSubpage(subpage.id)}
              >
                <div className="subpage-btn-content">
                  <span className="subpage-btn-main">{subpage.title}</span>
                  {subpage.subtitle && (
                    <span className="subpage-btn-sub">{subpage.subtitle}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Subpage Content */}
        {renderSubpageContent()}

        {/* Bibtex Section */}
        <div className="bibtex-section" ref={bibtexRef}>
          <h2 className="bibtex-title">Citation</h2>
          <div className="bibtex-box">
            <pre className="bibtex-content">{bibtexText}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
